import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuthStore, formatApiErrorDetail } from "@/lib/authStore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { TrendingUp } from "lucide-react";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, register } = useAuthStore();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ email: "", password: "", name: "" });

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (mode) => {
    setLoading(true);
    setError("");
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        toast.success("Welcome back!");
      } else {
        await register(form.email, form.password, form.name);
        toast.success("Account created!");
      }
      navigate("/profile");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <header className="bg-white border-b border-slate-100 accent-border-top">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center gap-3">
          <Link to="/" className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-lg bg-primary flex items-center justify-center">
              <TrendingUp className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-slate-900">Deal Velocity · Akamai</h1>
              <p className="text-xs text-slate-400">AI Sales Simulation</p>
            </div>
          </Link>
        </div>
      </header>

      <div className="flex-1 flex items-center justify-center px-4 py-12">
        <Card className="w-full max-w-md border-slate-200">
          <CardHeader>
            <CardTitle>Learner Account</CardTitle>
            <CardDescription>
              Sign in to track your scores across practice sessions.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Tabs defaultValue="login">
              <TabsList className="grid w-full grid-cols-2 mb-4">
                <TabsTrigger value="login" data-testid="login-tab">Sign In</TabsTrigger>
                <TabsTrigger value="register" data-testid="register-tab">Create Account</TabsTrigger>
              </TabsList>

              <TabsContent value="login" className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="login-email">Email</Label>
                  <Input id="login-email" type="email" value={form.email} onChange={setField("email")}
                    placeholder="you@company.com" data-testid="login-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="login-password">Password</Label>
                  <Input id="login-password" type="password" value={form.password} onChange={setField("password")}
                    onKeyDown={(e) => e.key === "Enter" && submit("login")} data-testid="login-password-input" />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="auth-error">{error}</p>}
                <Button className="w-full bg-primary hover:bg-primary/90" disabled={loading}
                  onClick={() => submit("login")} data-testid="login-submit-btn">
                  {loading ? "Signing in..." : "Sign In"}
                </Button>
                <p className="text-xs text-slate-400 text-center">
                  Demo account: demo@dealvelocity.com / Demo1234!
                </p>
              </TabsContent>

              <TabsContent value="register" className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="reg-name">Name</Label>
                  <Input id="reg-name" value={form.name} onChange={setField("name")}
                    placeholder="Alex Morgan" data-testid="register-name-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-email">Email</Label>
                  <Input id="reg-email" type="email" value={form.email} onChange={setField("email")}
                    placeholder="you@company.com" data-testid="register-email-input" />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="reg-password">Password</Label>
                  <Input id="reg-password" type="password" value={form.password} onChange={setField("password")}
                    onKeyDown={(e) => e.key === "Enter" && submit("register")} data-testid="register-password-input" />
                </div>
                {error && <p className="text-sm text-red-600" data-testid="auth-error-register">{error}</p>}
                <Button className="w-full bg-primary hover:bg-primary/90" disabled={loading}
                  onClick={() => submit("register")} data-testid="register-submit-btn">
                  {loading ? "Creating account..." : "Create Account"}
                </Button>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
